import { useRef } from 'react';

/**
 * Fallback buffer added to the exit timeout to account for timing imprecision
 * when the CSS `transitionend` event does not fire (e.g. reduced-motion, test
 * environments).
 */
export const EXIT_FALLBACK_BUFFER_MS = 20;

/**
 * Default animation duration used when the CSS transition duration cannot be
 * determined from the computed style of the element.
 */
export const DEFAULT_ANIMATION_DURATION_MS = 200;

/**
 * Represents the four discrete phases of a CSS enter/exit animation cycle.
 *
 * - `entering` — transition has been triggered but has not yet started (first rAF)
 * - `entered`  — element is fully visible
 * - `exiting`  — exit transition is in progress
 * - `exited`   — element is fully hidden
 */
export type AnimationState = 'entered' | 'entering' | 'exited' | 'exiting';

/**
 * Parses a CSS time value string into milliseconds.
 *
 * Handles both `ms` (e.g. `"200ms"`) and `s` (e.g. `"0.2s"`) units.
 * Returns `0` for empty or unrecognised values.
 *
 * @param value - Raw CSS time string from `getComputedStyle`.
 * @returns Duration in milliseconds.
 */
export const parseCssTimeToMs = (value: string): number => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 0;
  }

  if (trimmedValue.endsWith('ms')) {
    return Number.parseFloat(trimmedValue);
  }

  if (trimmedValue.endsWith('s')) {
    return Number.parseFloat(trimmedValue) * 1000;
  }

  return 0;
};

/**
 * Returns the maximum total transition time (duration + delay) across all CSS
 * transitions applied to `element`, in milliseconds.
 *
 * When no positive transition time is found the function falls back to the
 * `--rbui-modal-animation-duration` CSS custom property, and then to
 * {@link DEFAULT_ANIMATION_DURATION_MS}.
 *
 * @param element - The DOM element whose computed style is inspected.
 * @returns Maximum transition time in milliseconds.
 */
export const getMaxTransitionTimeMs = (element: HTMLDivElement | null): number => {
  if (!element) {
    return DEFAULT_ANIMATION_DURATION_MS;
  }

  const computedStyle = getComputedStyle(element);
  const transitionDurations = computedStyle.transitionDuration.split(',').map((value) => parseCssTimeToMs(value));
  const transitionDelays = computedStyle.transitionDelay.split(',').map((value) => parseCssTimeToMs(value));
  const fallbackDurationMs =
    parseCssTimeToMs(computedStyle.getPropertyValue('--rbui-modal-animation-duration')) ||
    DEFAULT_ANIMATION_DURATION_MS;

  const maxDuration = transitionDurations.reduce((maxDuration, duration, index) => {
    const delay = transitionDelays[index] ?? transitionDelays[0] ?? 0;
    return Math.max(maxDuration, duration + delay);
  }, 0);

  return maxDuration || fallbackDurationMs;
};

/**
 * Maps an {@link AnimationState} to a set of `data-*` attributes suitable for
 * spreading onto a DOM element.
 *
 * Only the attribute corresponding to the current `animationState` is set to
 * `true`; the remaining attributes are `undefined` so they are omitted from
 * the rendered HTML.
 *
 * @param animationState - The current animation phase.
 * @returns An object of `data-*` attribute key/value pairs.
 */
export const getAnimationStateAttributes = (animationState: AnimationState): Record<string, true | undefined> => ({
  'data-entered': animationState === 'entered' || undefined,
  'data-entering': animationState === 'entering' || undefined,
  'data-exited': animationState === 'exited' || undefined,
  'data-exiting': animationState === 'exiting' || undefined,
});

/**
 * Returns a ref whose `.current` is always synchronised to the latest value of
 * `value`.
 *
 * Useful for reading the most-recent version of a prop or callback inside a
 * stable `useEffect` or `useCallback` without listing it as a dependency,
 * avoiding stale-closure bugs.
 *
 * @param value - The value to keep up-to-date in the ref.
 * @returns A mutable ref object whose `.current` mirrors `value`.
 */
export const useLatestRef = <T>(value: T): { current: T } => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};
